import type { BankTransactionBase } from '@api/models';
import type { UpdateBankTransactionData } from '@api/resources/bank_transactions';
import { useDebounceFn } from '@vueuse/core';
import { ref, watch } from 'vue';
import type { Ref } from 'vue';

import { toast } from '@ui/components/sonner';

import { stuut } from '@/api';

export function useBankTransactionPaymentStatus(
  transaction: Ref<BankTransactionBase | undefined>,
  onUpdate: (transaction: BankTransactionBase) => void,
) {
  const updating = ref(false);
  const nonPaymentReason = ref(transaction.value?.non_payment_reason || '');

  // Watch for transaction changes to sync local reason
  watch(
    () => transaction.value?.non_payment_reason,
    (newReason) => {
      if (newReason !== nonPaymentReason.value) {
        nonPaymentReason.value = newReason || '';
      }
    },
  );

  async function setIsPayment(isPayment: boolean) {
    if (!transaction.value) return;

    updating.value = true;
    try {
      const data: UpdateBankTransactionData = {
        is_payment: isPayment,
      };

      // Include reason when setting to non-payment
      if (isPayment === false) {
        data.non_payment_reason = nonPaymentReason.value || null;
      }

      const updatedTransaction = await stuut.bankTransactions.update(transaction.value.id, {
        data,
      });

      onUpdate(updatedTransaction);
    } catch (error) {
      console.error('Failed to update is_payment status:', error);
    } finally {
      updating.value = false;
    }
  }

  const updateReason = useDebounceFn(async () => {
    if (!transaction.value || transaction.value.is_payment !== false) return;

    try {
      const updatedTransaction = await stuut.bankTransactions.update(transaction.value.id, {
        data: { non_payment_reason: nonPaymentReason.value || null },
      });

      onUpdate(updatedTransaction);
      toast.success('Non-payment reason saved');
    } catch (error) {
      console.error('Failed to update non_payment_reason:', error);
      toast.error('Failed to save non-payment reason');
    }
  }, 1500);

  watch(nonPaymentReason, () => {
    if (transaction.value?.is_payment === false) {
      updateReason();
    }
  });

  return {
    nonPaymentReason,
    setIsPayment,
    updating,
  };
}
